/* __next_internal_client_entry_do_not_use__ ExpandableText auto */ import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { TypographyStyles } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { LinkStyles } from "@faire/web--source/ui/Link";
import throttle from "lodash/throttle";
import React, { useEffect, useRef, useMemo, useState } from "react";
import styled, { css } from "styled-components";
var RESIZE_THROTTLE_MS = 200;
var LINE_HEIGHT = 20;
export var ExpandableText = function(props) {
    var _useState = _sliced_to_array(useState(0), 2), textHeight = _useState[0], setTextHeight = _useState[1];
    var _useState1 = _sliced_to_array(useState(false), 2), isExpanded = _useState1[0], setIsExpanded = _useState1[1];
    var content = props.content, maxLines = props.maxLines, ctaColor = props.ctaColor, readMoreBgColor = props.readMoreBgColor, collapsible = props.collapsible, expandButtonLabel = props.expandButtonLabel, collapseButtonLabel = props.collapseButtonLabel, color = props.color;
    var updateTextHeight = function() {
        var _measureRef_current;
        var _measureRef_current_clientHeight;
        setTextHeight((_measureRef_current_clientHeight = (_measureRef_current = measureRef.current) === null || _measureRef_current === void 0 ? void 0 : _measureRef_current.clientHeight) !== null && _measureRef_current_clientHeight !== void 0 ? _measureRef_current_clientHeight : 0);
    };
    var throttledUpdateTextHeight = throttle(updateTextHeight, RESIZE_THROTTLE_MS);
    useEffect(function() {
        var _getWindow;
        (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.addEventListener("resize", throttledUpdateTextHeight);
        updateTextHeight();
        return function() {
            var _getWindow;
            (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.removeEventListener("resize", throttledUpdateTextHeight);
        };
    });
    var onExpand = function(event) {
        var _props_expandCallback;
        event.preventDefault();
        event.stopPropagation();
        setIsExpanded(true);
        (_props_expandCallback = props.expandCallback) === null || _props_expandCallback === void 0 ? void 0 : _props_expandCallback.call(props);
    };
    var onCollapse = function(event) {
        var _props_collapseCallback;
        event.preventDefault();
        event.stopPropagation();
        setIsExpanded(false);
        (_props_collapseCallback = props.collapseCallback) === null || _props_collapseCallback === void 0 ? void 0 : _props_collapseCallback.call(props);
    };
    var canExpand = useMemo(function() {
        return !isExpanded && textHeight > LINE_HEIGHT * props.maxLines;
    }, [
        textHeight,
        isExpanded,
        props.maxLines
    ]);
    var measureRef = useRef(null);
    return /*#__PURE__*/ _jsxs("div", {
        style: {
            position: "relative"
        },
        children: [
            /*#__PURE__*/ _jsx(InvisibleText, {
                ref: measureRef,
                $maxLines: maxLines,
                $collapsible: collapsible,
                $expanded: true,
                color: color,
                children: content
            }),
            /*#__PURE__*/ _jsx(Text, {
                $expanded: isExpanded,
                $maxLines: maxLines,
                $collapsible: collapsible,
                color: color,
                children: content
            }),
            isExpanded && collapsible ? /*#__PURE__*/ _jsx(LessButton, {
                onClick: onCollapse,
                color: ctaColor,
                children: collapseButtonLabel ? collapseButtonLabel : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "WjfdZ4",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Close"
                        }
                    ]
                })
            }) : null,
            canExpand ? /*#__PURE__*/ _jsx(MoreButton, {
                $topPosition: (maxLines - 1) * LINE_HEIGHT,
                onClick: onExpand,
                color: ctaColor,
                $readMoreBgColor: readMoreBgColor,
                children: expandButtonLabel ? expandButtonLabel : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "/cPjWY",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Read More"
                        }
                    ]
                })
            }) : null
        ]
    });
};
var Text = styled.p.withConfig({
    componentId: "sc-2ecce23c-0"
})([
    "",
    ";display:-webkit-box;-webkit-line-clamp:",
    ";-webkit-box-orient:vertical;overflow:hidden;word-break:break-word;",
    ";",
    ";"
], function(props) {
    return TypographyStyles({
        color: props.color,
        theme: props.theme
    });
}, function(param) {
    var $maxLines = param.$maxLines;
    return $maxLines;
}, function(param) {
    var $expanded = param.$expanded;
    return $expanded ? css([
        "-webkit-line-clamp:unset;-webkit-box-orient:unset;"
    ]) : "";
}, function(param) {
    var $expanded = param.$expanded, $collapsible = param.$collapsible;
    return $expanded && $collapsible ? css([
        "display:inline;"
    ]) : "";
});
var InvisibleText = styled(Text).withConfig({
    componentId: "sc-2ecce23c-1"
})([
    "position:absolute;bottom:0;opacity:0;z-index:-1;"
]);
var MoreButton = styled.div.withConfig({
    componentId: "sc-2ecce23c-2"
})([
    "",
    " ",
    " ",
    " position:absolute;top:",
    'px;right:0;z-index:1;&::before{content:"";position:absolute;top:0;left:-40px;right:0;bottom:0;z-index:-1;cursor:unset;pointer-events:none;background:linear-gradient( 90deg,rgba(255,255,255,0),',
    " 30% );}"
], function(props) {
    return TypographyStyles({
        theme: props.theme
    });
}, function(props) {
    return LinkStyles({
        color: props.color
    });
}, LinkStyles, function(props) {
    return props.$topPosition;
}, function(props) {
    var _props_$readMoreBgColor;
    return (_props_$readMoreBgColor = props.$readMoreBgColor) !== null && _props_$readMoreBgColor !== void 0 ? _props_$readMoreBgColor : Core.surface.inverse;
});
var LessButton = styled.div.withConfig({
    componentId: "sc-2ecce23c-3"
})([
    "",
    " ",
    " display:inline;padding-left:",
    ";"
], function(props) {
    return TypographyStyles({
        theme: props.theme
    });
}, function(props) {
    return LinkStyles({
        color: props.color
    });
}, getSpacing("1x"));
